import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { DateTime } from 'luxon';
import { Box, Grid, Link } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { Delete, Download, ThumbUpAlt } from '@mui/icons-material';
import Currency from 'components/formaters/Currency';
import Date from 'components/formaters/Date';
import { InvoiceModel, InvoiceStatus } from 'models/InvoiceModel';
import useInvoices from 'modules/invoices/hooks/useInvoices';
import useInvoiceModals from 'modules/invoices/hooks/useInvoiceModals';
import { InvoiceLink } from 'modules/invoices/common/Components';
import VoyageLink from 'modules/cars/common/VoyageLink';
import auth from 'recoil/atoms/auth';


const Inner = () => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const {openInvoiceConfirmationModal, confirmDeleteInvoice} = useInvoiceModals();
    const [invoices, setInvoices] = useState<InvoiceModel[]>([]);
    const {findInvoices} = useInvoices();

    const load = () => {
        findInvoices({
            status: InvoiceStatus.Unpaid,
            payer: 'consignee',
            limit: 1000,
        })
            .then(res => setInvoices(res.data))
    };

    // eslint-disable-next-line
    useEffect(load, []);

    const confirmButton = (invoice: InvoiceModel) => (
        <GridActionsCellItem
            icon={<ThumbUpAlt/>}
            label={t('confirm')}
            onClick={() => openInvoiceConfirmationModal(invoice, invoices).then(load)}
            showInMenu
        />
    );

    // TODO replace this and other Link with component after https://github.com/mui/mui-x/issues/4654
    const viewButton = (invoice: InvoiceModel) => (
        <Link component="a" target="_blank" href={`${process.env.REACT_APP_API_HOST}/invoices/${invoice.uuid}`}>
            <GridActionsCellItem
                icon={<Download/>}
                label={t('view')}
            />
        </Link>
    );

    const deleteButton = (invoice: InvoiceModel) => (
        <GridActionsCellItem
            icon={<Delete/>}
            label={t('delete')}
            onClick={() => confirmDeleteInvoice(invoice).then(load)}
            showInMenu
        />
    );

    const columns: GridColDef<InvoiceModel>[] = [
        {
            field: 'payer',
            flex: 1,
            renderCell: params => <Box width="100%">
                <strong>{params.row.payer.name}</strong>

                <Grid container>
                    <Grid item xs={6}>
                        <InvoiceLink invoice={params.row}/>
                    </Grid>
                    <Grid item xs={6}>
                        <Box textAlign="right">
                            <Currency value={params.row.grandTotal} currency={params.row.currency}/>
                            <br/>
                            {params.row.paid > 0 && params.row.paid < params.row.grandTotal && (
                                <Box sx={{color: 'text.disabled'}}>
                                    {t('paid')} <Currency value={params.row.paid} currency={params.row.currency}/>
                                </Box>
                            )}
                        </Box>
                    </Grid>
                </Grid>

                {t('due_date')}:
                {' '}
                <Date date={DateTime.fromISO(params.row.dueDate)}/>

                {params.row.type === 'freight' && <VoyageLink voyage={params.row.voyage}/>}
            </Box>,
        },
        {
            field: 'actions',
            type: 'actions',
            getActions: params => {
                const act = [
                    viewButton(params.row),
                ];
                if (my.permissions.has('invoices update')) act.push(confirmButton(params.row));
                if (my.permissions.has('invoices delete')) act.push(deleteButton(params.row));
                return act;
            },
        },
    ];

    return (
        <Box sx={{height: 'calc(100vh - 148px)', bgcolor: 'background.paper'}}>
            <DataGrid
                rows={invoices}
                columns={columns}
                rowHeight={104}
                pageSizeOptions={[100]}
                disableRowSelectionOnClick
                slots={{
                    columnHeaders: () => null,
                }}
            />
        </Box>
    );
};

const FobUnpaidList = () => {
    return (
        <Suspense fallback={null}>
            <Inner/>
        </Suspense>
    )
}

export default FobUnpaidList;
